// src/features/executive/performance/widgets/RevenueTrendCard.tsx
import { Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
  Legend,
  BarController,
} from 'chart.js'
import { useDispatch } from 'react-redux'
import { pushDrill } from 'app/slice/executive.slice'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { useRevenueTrend } from '../hooks/use-revenue-trend'
import { formatCurrency, formatDateThaiShort } from 'utils/executive.utils'
import { legendSortByDatasetIndex, REVENUE_TREND_ORDER } from 'features/executive/performance/widgets/revenue-trend-layers'
import { colors } from 'constants/theme'

// Expand a chart bucket label (YYYY-MM or YYYY-MM-DD) to a full ISO range.
// YYYY-MM → first day to last day of that month.
// YYYY-MM-DD → same day (no-op).
function expandBucketRange(bucket: string): { start: string; end: string } {
  if (bucket.length === 7) {
    const [y, m] = bucket.split('-').map(Number)
    const last = new Date(y, m, 0).getDate()
    const mm = String(m).padStart(2, '0')
    const dd = String(last).padStart(2, '0')
    return { start: `${y}-${mm}-01`, end: `${y}-${mm}-${dd}` }
  }
  return { start: bucket, end: bucket }
}

ChartJS.register(
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  ChartTooltip,
  Legend
)

// When the user toggles every legend series off, chart.js keeps drawing
// empty axes and auto-scales the y-axis to 0…1 (which formatCurrency
// rounds into a duplicate ฿0/฿1 ladder). Two-part fix:
//   1. beforeLayout: hide the y-axis so those bogus ticks disappear.
//   2. afterDraw: paint a clear message onto the plot area pointing the
//      user back at the legend — the chart (and its legend) stays mounted
//      so a single click restores whichever series they want.
const legendHiddenOverlayPlugin = {
  id: 'revenueTrendLegendHiddenOverlay',
  beforeLayout(chart: any) {
    const anyVisible = chart.data.datasets.some((_: any, i: number) => chart.isDatasetVisible(i))
    if (chart.options.scales?.y) {
      chart.options.scales.y.display = anyVisible
    }
  },
  afterDraw(chart: any) {
    const anyVisible = chart.data.datasets.some((_: any, i: number) => chart.isDatasetVisible(i))
    if (anyVisible) return
    const { ctx, chartArea } = chart
    if (!chartArea) return
    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#616161'
    const cx = (chartArea.left + chartArea.right) / 2
    const cy = (chartArea.top + chartArea.bottom) / 2
    ctx.font = '600 15px "Sarabun", sans-serif'
    ctx.fillText('ชุดข้อมูลถูกซ่อนทั้งหมด', cx, cy - 10)
    ctx.font = '400 13px "Sarabun", sans-serif'
    ctx.fillStyle = '#9e9e9e'
    ctx.fillText('คลิกที่ป้ายชื่อด้านล่างเพื่อแสดงข้อมูลอีกครั้ง', cx, cy + 12)
    ctx.restore()
  },
}

export default function RevenueTrendCard() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { data, loading, error, refetch } = useRevenueTrend()

  const title = t('EXECUTIVE.PERFORMANCE.REVENUE.TREND_TITLE')
  // Treat "no buckets" AND "every bucket is sub-baht on both series"
  // as empty. Without this, chart.js auto-scales a flat-zero (or a
  // sub-baht target, e.g. monthly target 30฿ ÷ 31 days ≈ 0.97) dataset
  // and renders duplicate ticks (฿0, ฿0, ฿0, ฿1, ฿1, ฿1) rounded from
  // 0…1 instead of showing the widget's empty state. Use < 1 (not === 0)
  // so tiny fractional targets from the monthly-÷-days derivation trip
  // the empty branch too.
  const isEmpty = !data
    || data.points.length === 0
    || data.points.every((p) => (!p.actual || p.actual < 1) && (p.target == null || p.target < 1))

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).tagName === 'CANVAS') return
    dispatch(pushDrill({ id: 'revenue', label: title }))
  }

  const chartData = useMemo(() => {
    if (!data) return null
    return {
      labels: data.points.map((p) => formatDateThaiShort(p.bucket)),
      datasets: [
        {
          type: 'bar' as const,
          label: t('EXECUTIVE.PERFORMANCE.REVENUE.TITLE'),
          data: data.points.map((p) => p.actual),
           backgroundColor: '#2a8d9c',
          /* MD-2610-1383: แท่งอยู่หลังเส้นเป้าหมาย (ดู revenue-trend-layers) */
          order: REVENUE_TREND_ORDER.bar,
        },
        {
          type: 'line' as const,
          label: t('EXECUTIVE.PERFORMANCE.REVENUE.TARGET'),
          data: data.points.map((p) => p.target ?? null),
          borderColor: '#e67e22',
          borderDash: [6, 4],
          pointRadius: 0,
          fill: false,
          spanGaps: true,
          order: REVENUE_TREND_ORDER.targetLine,
        },
      ],
    }
  }, [data, t])

  return (
    <WidgetCard
      title={title}
      titleTooltip={t(
        'EXECUTIVE.PERFORMANCE.REVENUE.TREND_TOOLTIP',
        'แนวโน้มรายได้รวม (สุทธิ) เทียบเป้าหมาย ตามช่วงเวลา\n• แท่ง = รายได้รวม (สุทธิ) — เงื่อนไขเดียวกับ KPI "รายได้รวม (สุทธิ)"\n• เส้นประ = เป้าหมาย เฉลี่ยจากเป้ารายเดือน (เป้าเดือน ÷ จำนวนวันในเดือน)\n• ช่วงเวลายาวขึ้น แท่งและเป้าจะรวมเป็นราย สัปดาห์/เดือน อัตโนมัติ'
      )}
      loading={loading}
      error={error}
      empty={isEmpty}
      onRetry={refetch}
      onClick={handleCardClick}
      skeletonHeight={340}
    >
      <Box sx={{ height: 280 }}>
        <Bar
          data={chartData as any}
          plugins={[legendHiddenOverlayPlugin]}
          options={{
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: { position: 'bottom', labels: { sort: legendSortByDatasetIndex } },
              tooltip: {
                callbacks: {
                  label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`,
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: (v) => formatCurrency(Number(v)),
                },
              },
            },
            onClick: (_evt, elements) => {
              if (!elements || elements.length === 0) return
              const bucket = data?.points[elements[0].index]?.bucket
              if (!bucket) return
              dispatch(pushDrill({
                id: 'revenue',
                label: `${title} · ${formatDateThaiShort(bucket)}`,
                scopedFilters: { dateRange: { preset: 'custom', ...expandBucketRange(bucket) } },
              }))
            },
          }}
        />
      </Box>
    </WidgetCard>
  )
}
